import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, Users, Calendar, FileText, DollarSign, List, LogOut, UserCog, 
  Package, Settings, Building2, Code, FileStack, Tag, CalendarClock, 
  Wallet, MapPin, ChevronDown, ChevronRight, Cog, Shield, MessageSquare, 
  ClipboardList, Crown, HelpCircle, Bug, Lightbulb, Star, FolderCog, 
  Percent, Receipt, Mail, Wrench, Send, Music, Download, BarChart3, Globe, Server 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Sidebar({ user, onLogout, isEmployee = false, permissions = null, employeeInfo = null, onNavigate = null }) {
  const location = useLocation();
  const [configOpen, setConfigOpen] = useState(
    ['/packages', '/event-types', '/venue-types', '/contract-templates', '/forms', '/task-settings'].includes(location.pathname)
  );
  const [settingsOpen, setSettingsOpen] = useState(
    ['/settings', '/roles', '/widgets', '/analytics', '/platform-accounts'].includes(location.pathname)
  );
  const [supportOpen, setSupportOpen] = useState(
    ['/support/features', '/support/bugs', '/support/feature-requests', '/support/categories'].includes(location.pathname)
  );
  const [unreadCount, setUnreadCount] = useState(0);
  const [tasksDueCount, setTasksDueCount] = useState(0);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoError, setLogoError] = useState(false);

  // Fetch unread message count, tasks due count, and company logo
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [messagesRes, tasksRes] = await Promise.all([
          axios.get(`${API}/messages/unread-count`),
          axios.get(`${API}/tasks/count`)
        ]);
        setUnreadCount(messagesRes.data?.unread_count || 0);
        setTasksDueCount((tasksRes.data?.overdue_count || 0) + (tasksRes.data?.due_today_count || 0));
      } catch (error) {
        console.error('Failed to fetch counts:', error);
      }
    };

    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        if (response.data?.logo) {
          setCompanyLogo(response.data.logo);
          setLogoError(false);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };

    fetchCounts();
    fetchSettings();
    // Refresh counts every 30 seconds
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Permission check helper
  const hasPermission = (permission) => {
    if (!isEmployee) return true; // Owner has all permissions
    if (!permissions) return false;
    if (permissions.administrator) return true;
    return permissions[permission] === true;
  };

  // Filter nav items based on permissions
  const mainNavItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard', show: true },
    { path: '/contacts', icon: Users, label: 'Contacts', show: hasPermission('view_contacts') || hasPermission('add_leads') || hasPermission('view_all_events') },
    { path: '/bookings', icon: Calendar, label: 'Bookings', show: hasPermission('view_all_events') || hasPermission('view_assigned_events') },
    { path: '/messages', icon: MessageSquare, label: 'Messages', show: hasPermission('send_emails') || hasPermission('view_all_events') || hasPermission('view_assigned_events'), badge: unreadCount },
    { path: '/tasks', icon: ClipboardList, label: 'Tasks', show: hasPermission('view_all_events') || hasPermission('view_assigned_events'), badge: tasksDueCount, badgeColor: 'orange' },
    { path: '/employees', icon: UserCog, label: 'Employees', show: hasPermission('manage_employees') },
    { path: '/contracts', icon: FileText, label: 'Contracts', show: hasPermission('view_all_events') || hasPermission('edit_bookings') },
    { path: '/invoices', icon: DollarSign, label: 'Invoices', show: hasPermission('accept_payments') || hasPermission('view_accounting') },
    { path: '/accounts', icon: Wallet, label: 'Accounting', show: hasPermission('view_accounting') },
    { path: '/vendors-venues', icon: Building2, label: 'Vendors & Venues', show: hasPermission('access_configuration') },
    { path: '/equipment', icon: Wrench, label: 'Equipment', show: hasPermission('access_equipment') || hasPermission('access_configuration') },
    { path: '/music-library', icon: Music, label: 'Music Library', show: hasPermission('access_music_library') || hasPermission('access_configuration') },
    { path: '/email-campaigns', icon: Send, label: 'Email Campaigns', show: hasPermission('send_emails') || hasPermission('access_settings') },
    { path: '/availability', icon: CalendarClock, label: 'Availability', show: hasPermission('access_settings') || hasPermission('view_all_events') },
  ].filter(item => item.show);

  const configItems = [
    { path: '/configuration/packages', icon: Package, label: 'Packages' },
    { path: '/configuration/event-types', icon: Tag, label: 'Event Types' },
    { path: '/configuration/venue-types', icon: MapPin, label: 'Venue Types' },
    { path: '/configuration/contract-templates', icon: FileStack, label: 'Contract Templates' },
    { path: '/configuration/invoice-templates', icon: Receipt, label: 'Invoice Templates' },
    { path: '/configuration/email-templates', icon: Mail, label: 'Email Templates' },
    { path: '/configuration/forms', icon: List, label: 'Forms' },
    { path: '/configuration/discounts', icon: Percent, label: 'Discounts' },
    { path: '/configuration/task-settings', icon: ClipboardList, label: 'Tasks' },
  ];

  // Base settings items
  const baseSettingsItems = [
    { path: '/settings', icon: Settings, label: 'Settings' },
    { path: '/roles', icon: Shield, label: 'Roles' },
    { path: '/website-builder', icon: Globe, label: 'Website' },
    { path: '/widgets', icon: Code, label: 'Website Widgets' },
    { path: '/analytics', icon: BarChart3, label: 'Analytics' },
  ];
  
  // Add Platform Accounts and Frontend Settings for platform admins only
  const settingsItems = user?.is_platform_admin 
    ? [...baseSettingsItems, 
       { path: '/frontend-settings', icon: Globe, label: 'Frontend' },
       { path: '/platform-accounts', icon: Crown, label: 'Platform Accounts' },
       { path: '/platform-settings', icon: Server, label: 'Platform Email' },
       { path: '/platform-export', icon: Download, label: 'Data Export' }
      ]
    : baseSettingsItems;

  // Support items - available to all users, but Categories only for platform admin
  const baseSupportItems = [
    { path: '/support/features', icon: Star, label: 'Features' },
    { path: '/support/bugs', icon: Bug, label: 'Bug Reports' },
    { path: '/support/feature-requests', icon: Lightbulb, label: 'Feature Requests' },
  ];
  
  const supportItems = user?.is_platform_admin 
    ? [...baseSupportItems, { path: '/support/categories', icon: FolderCog, label: 'Categories' }]
    : baseSupportItems;

  const showConfiguration = hasPermission('access_configuration');
  const showSettings = hasPermission('access_settings');

  const isConfigActive = configItems.some(item => location.pathname === item.path);
  const isSettingsActive = settingsItems.some(item => location.pathname === item.path) || location.pathname === '/platform-accounts' || location.pathname === '/platform-settings' || location.pathname === '/website-builder';
  const isSupportActive = supportItems.some(item => location.pathname === item.path);

  const renderNavItem = (item, isSubItem = false) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    const badgeColorClass = item.badgeColor === 'orange' 
      ? 'bg-orange-500' 
      : 'bg-red-500';
    return (
      <Link
        key={item.path}
        to={item.path}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        onClick={() => onNavigate && onNavigate()}
      >
        <div
          className={`flex items-center gap-3 ${isSubItem ? 'px-4 py-2.5 ml-4' : 'px-4 py-3'} rounded-xl transition-all ${
            isActive
              ? 'bg-neon-blue/20 text-neon-blue'
              : 'hover:bg-black/5 dark:hover:bg-white/5 text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon className={`${isSubItem ? 'w-4 h-4' : 'w-5 h-5'}`} />
          <span className={`${isSubItem ? 'text-sm' : ''} font-medium flex-1`}>{item.label}</span>
          {item.badge > 0 && (
            <span className={`${badgeColorClass} text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center`}>
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          )}
        </div>
      </Link>
    );
  };

  return (
    <div className="w-64 glass-card border-r border-glass-border h-screen flex flex-col" data-testid="sidebar">
      <div className="p-6 border-b border-glass-border">
        {/* Impersonation Banner */}
        {localStorage.getItem('impersonating') === 'true' && (
          <div className="mb-3 p-2 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
            <p className="text-xs text-yellow-400 font-medium flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Viewing as: {user?.company_name}
            </p>
            <button
              onClick={() => {
                localStorage.removeItem('impersonating');
                window.close();
              }}
              className="text-xs text-yellow-400 hover:text-yellow-300 underline mt-1"
            >
              Close this session
            </button>
          </div>
        )}
        <div className="flex items-center gap-3">
          {companyLogo && !logoError ? (
            <img 
              src={companyLogo} 
              alt="Company Logo" 
              className="h-10 w-auto object-contain"
              data-testid="sidebar-logo-img"
              onError={() => setLogoError(true)}
            />
          ) : (
            <h1 className="font-heading text-2xl font-bold text-neon-blue" data-testid="sidebar-logo">DJ CRM</h1>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1">{user?.company_name}</p>
        {isEmployee && employeeInfo && (
          <p className="text-xs text-neon-purple mt-1 flex items-center gap-1">
            <Shield className="w-3 h-3" />
            {employeeInfo.name}
          </p>
        )}
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {mainNavItems.map((item) => renderNavItem(item))}

        {/* Configuration Section - Only show if has access_configuration permission */}
        {showConfiguration && (
          <div className="pt-2">
            <button
              onClick={() => setConfigOpen(!configOpen)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                isConfigActive
                  ? 'bg-neon-purple/20 text-neon-purple'
                  : 'hover:bg-black/5 dark:hover:bg-white/5 text-muted-foreground hover:text-foreground'
              }`}
              data-testid="nav-configuration"
            >
              <div className="flex items-center gap-3">
                <Cog className="w-5 h-5" />
                <span className="font-medium">Configuration</span>
              </div>
              {configOpen ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
            
            {configOpen && (
              <div className="mt-1 space-y-1 border-l-2 border-glass-border ml-6">
                {configItems.map((item) => renderNavItem(item, true))}
              </div>
            )}
          </div>
        )}

        {/* Settings Section - Only show if has access_settings permission */}
        {showSettings && (
          <div className="pt-2">
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                isSettingsActive
                  ? 'bg-neon-blue/20 text-neon-blue'
                  : 'hover:bg-black/5 dark:hover:bg-white/5 text-muted-foreground hover:text-foreground'
              }`}
              data-testid="nav-settings-section"
            >
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5" />
                <span className="font-medium">Settings</span>
              </div>
              {settingsOpen ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
            
            {settingsOpen && (
              <div className="mt-1 space-y-1 border-l-2 border-glass-border ml-6">
                {settingsItems.map((item) => renderNavItem(item, true))}
              </div>
            )}
          </div>
        )}

        {/* Support Section - Available to all users */}
        <div className="pt-2">
          <button
            onClick={() => setSupportOpen(!supportOpen)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
              isSupportActive
                ? 'bg-yellow-500/20 text-yellow-500'
                : 'hover:bg-black/5 dark:hover:bg-white/5 text-muted-foreground hover:text-foreground'
            }`}
            data-testid="nav-support-section"
          >
            <div className="flex items-center gap-3">
              <HelpCircle className="w-5 h-5" />
              <span className="font-medium">Support</span>
            </div>
            {supportOpen ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          
          {supportOpen && (
            <div className="mt-1 space-y-1 border-l-2 border-glass-border ml-6">
              {supportItems.map((item) => renderNavItem(item, true))}
            </div>
          )}
        </div>
      </nav>

      <div className="p-4 border-t border-glass-border">
        <Button
          onClick={onLogout}
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-foreground"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-3" />
          Logout
        </Button>
      </div>
    </div>
  );
}
